import type { SimulationInputs, SimulationSummary, StrategyKind } from '../types'
import { runSimulationSummary, validateSimulationInputs } from './monteCarlo'
import { getStrategyMeta, STRATEGY_META } from './strategy'

export type OptimizationParameter =
  | 'dte'
  | 'targetDelta'
  | 'otmPct'
  | 'fixedCallStrike'
  | 'fixedPutStrike'
  | 'notionalMultiple'
  | 'impliedVol'
  | 'realizedVol'
  | 'drift'
  | 'longRunIv'
  | 'skewSlope'
  | 'commissionPerContract'
  | 'optionSlippagePct'

interface OptimizationParameterMeta {
  key: OptimizationParameter
  label: string
  unit?: string
  /** Multiply engine values and bounds by this factor for display. */
  scale: number
  min: number
  max: number
  integer?: boolean
}

export const OPTIMIZATION_PARAMETERS: OptimizationParameterMeta[] = [
  { key: 'dte', label: '期限 DTE', unit: '天', scale: 1, min: 1, max: 3650, integer: true },
  { key: 'targetDelta', label: 'Target |Delta|', scale: 1, min: 0.0001, max: 0.5 },
  { key: 'otmPct', label: 'OTM', unit: '%', scale: 100, min: 0, max: 0.9999 },
  { key: 'fixedCallStrike', label: 'Call 执行价', scale: 1, min: 0.01, max: 1e9 },
  { key: 'fixedPutStrike', label: 'Put 执行价', scale: 1, min: 0.01, max: 1e9 },
  { key: 'notionalMultiple', label: '名义本金 / Equity', unit: '×', scale: 1, min: 0.01, max: 100 },
  { key: 'impliedVol', label: 'Entry IV', unit: '%', scale: 100, min: 0.01, max: 5 },
  { key: 'realizedVol', label: 'Realized Vol', unit: '%', scale: 100, min: 0, max: 5 },
  { key: 'drift', label: 'Drift μ', unit: '%', scale: 100, min: -10, max: 10 },
  { key: 'longRunIv', label: '长期 IV', unit: '%', scale: 100, min: 0.01, max: 5 },
  { key: 'skewSlope', label: 'Skew slope', scale: 1, min: -10, max: 10 },
  { key: 'commissionPerContract', label: '佣金 / contract', scale: 1, min: 0, max: 1000 },
  { key: 'optionSlippagePct', label: 'Option slippage', unit: '%', scale: 100, min: 0, max: 1 },
]

export interface OptimizationAxis {
  parameter: OptimizationParameter
  /** Actual engine units: for example, 30% IV is 0.3. */
  values: number[]
}

export interface OptimizationConfig {
  strategies: StrategyKind[]
  axes: OptimizationAxis[]
  paths: number
}

export interface OptimizationCandidate {
  id: string
  label: string
  inputs: SimulationInputs
}

export interface OptimizationRow extends OptimizationCandidate {
  summary: SimulationSummary
}

export interface OptimizationResult {
  base: SimulationInputs
  config: OptimizationConfig
  rows: OptimizationRow[]
  elapsedMs: number
}

export type OptimizationObjective =
  | 'meanStrategyReturn'
  | 'averageExcessReturn'
  | 'meanPathSharpe'
  | 'meanMaxDrawdown'
  | 'expectedShortfall05'
  | 'probabilityLoss'

export const OPTIMIZATION_OBJECTIVES: {
  key: OptimizationObjective
  label: string
  direction: 'asc' | 'desc'
  format: 'percent' | 'number'
}[] = [
  { key: 'meanStrategyReturn', label: '平均策略收益', direction: 'desc', format: 'percent' },
  { key: 'averageExcessReturn', label: '平均超额收益', direction: 'desc', format: 'percent' },
  { key: 'meanPathSharpe', label: '平均路径 Sharpe', direction: 'desc', format: 'number' },
  { key: 'meanMaxDrawdown', label: '平均最大回撤', direction: 'asc', format: 'percent' },
  { key: 'expectedShortfall05', label: '最差 5% 平均收益', direction: 'desc', format: 'percent' },
  { key: 'probabilityLoss', label: '亏损概率', direction: 'asc', format: 'percent' },
]

export const MAX_OPTIMIZATION_COMBINATIONS = 250
export const MAX_OPTIMIZATION_PATH_DAYS = 100_000_000

const parameterMeta = new Map(OPTIMIZATION_PARAMETERS.map((entry) => [entry.key, entry]))

function isActiveParameter(parameter: OptimizationParameter, inputs: SimulationInputs): boolean {
  switch (parameter) {
    case 'targetDelta': return inputs.strikeMethod === 'delta'
    case 'otmPct': return inputs.strikeMethod === 'otm'
    case 'fixedCallStrike':
      return inputs.strikeMethod === 'fixed' && getStrategyMeta(inputs.strategy).optionLegs.some((leg) => leg.optionType === 'call')
    case 'fixedPutStrike':
      return inputs.strikeMethod === 'fixed' && getStrategyMeta(inputs.strategy).optionLegs.some((leg) => leg.optionType === 'put')
    case 'longRunIv': return inputs.ivModel !== 'constant' && inputs.ivMeanReversion > 0
    default: return true
  }
}

function validateBase(base: SimulationInputs): void {
  if (!base || typeof base !== 'object') throw new Error('缺少基础模拟参数。')
  validateSimulationInputs(base)
  const enums: [keyof SimulationInputs, readonly string[]][] = [
    ['strategy', Object.keys(STRATEGY_META)],
    ['strikeMethod', ['delta', 'otm', 'atm', 'fixed']],
    ['sizingMode', ['fixed_units', 'equity_rebalance']],
    ['contractRounding', ['fractional', 'floor', 'nearest']],
    ['insolvencyMode', ['continue_fixed_units', 'stop_new_risk']],
    ['settlementStyle', ['cash', 'physical_expiry']],
    ['marginMode', ['off', 'reg_t_approx']],
    ['ivModel', ['constant', 'mean_reverting', 'stress_regime']],
  ]
  for (const [field, choices] of enums) {
    if (!choices.includes(String(base[field]))) throw new Error(`${field} 参数无效。`)
  }
  if (base.ivModel !== 'constant' && !(base.longRunIv > 0)) throw new Error('长期 IV 必须大于 0。')
  if (base.ivModel === 'stress_regime' && !(base.stressIv > 0)) throw new Error('Stress IV 必须大于 0。')
}

/** Expands the effective Cartesian grid without mutating the base or requested axes. */
export function buildOptimizationCandidates(
  base: SimulationInputs,
  config: OptimizationConfig,
): OptimizationCandidate[] {
  validateBase(base)
  if (!config || typeof config !== 'object') throw new Error('缺少 Optimization 配置。')
  if (!Number.isInteger(config.paths) || config.paths < 100 || config.paths > 10000) {
    throw new Error('每组路径数必须为 100–10000 的整数。')
  }
  if (!Array.isArray(config.strategies) || !config.strategies.length) throw new Error('至少选择一个策略。')
  const strategies = new Set<StrategyKind>()
  for (const strategy of config.strategies) {
    if (!Object.hasOwn(STRATEGY_META, strategy)) throw new Error('策略无效。')
    if (strategies.has(strategy)) throw new Error('策略不可重复。')
    strategies.add(strategy)
  }
  if (!Array.isArray(config.axes)) throw new Error('参数网格必须为数组。')
  const parameters = new Set<OptimizationParameter>()
  const axes = config.axes.map((axis): OptimizationAxis => {
    if (!axis || typeof axis !== 'object') throw new Error('参数网格无效。')
    const meta = parameterMeta.get(axis.parameter)
    if (!meta) throw new Error('不支持的优化参数。')
    if (parameters.has(axis.parameter)) throw new Error(`${meta.label} 不可重复设置。`)
    parameters.add(axis.parameter)
    if (!Array.isArray(axis.values) || !axis.values.length) throw new Error(`${meta.label} 至少需要一个值。`)
    for (const value of axis.values) {
      if (typeof value !== 'number' || !Number.isFinite(value) || value < meta.min || value > meta.max || (meta.integer && !Number.isInteger(value))) {
        throw new Error(`${meta.label} 必须为 ${meta.min * meta.scale}–${meta.max * meta.scale}${meta.unit ?? ''} 范围内的${meta.integer ? '整数' : '有限数值'}。`)
      }
    }
    const effectiveValues = new Set<number>()
    const values = axis.values.filter((value) => {
      const effectiveValue = axis.parameter === 'dte' ? Math.max(1, Math.round(value / 365 * 252)) : value
      if (effectiveValues.has(effectiveValue)) return false
      effectiveValues.add(effectiveValue)
      return true
    })
    return { parameter: axis.parameter, values }
  })

  const candidates: OptimizationCandidate[] = []
  const totalDays = Math.max(1, Math.round(base.horizonYears * 252))
  for (const strategy of strategies) {
    const inputs = { ...base, strategy, paths: config.paths }
    const straddle = strategy === 'long_straddle' || strategy === 'short_straddle'
    if (straddle) inputs.strikeMethod = 'atm'
    // Remove inactive dimensions before expanding, so an irrelevant grid cannot
    // multiply either the computation or the effective workload estimate.
    const activeAxes = axes.filter((axis) => isActiveParameter(axis.parameter, inputs))
    const combinations = activeAxes.reduce((count, axis) => count * axis.values.length, 1)
    const nextCount = candidates.length + combinations
    if (nextCount > MAX_OPTIMIZATION_COMBINATIONS) {
      throw new Error(`有效组合最多 ${MAX_OPTIMIZATION_COMBINATIONS} 组，请减少策略或参数取值。`)
    }
    if (nextCount * config.paths * totalDays > MAX_OPTIMIZATION_PATH_DAYS) {
      throw new Error(`总计算量超过 ${MAX_OPTIMIZATION_PATH_DAYS.toLocaleString('en-US')} 路径·交易日，请减少组合、路径数或模拟年限。`)
    }

    const expand = (index: number, current: SimulationInputs, labels: string[]) => {
      if (index === activeAxes.length) {
        validateSimulationInputs(current)
        candidates.push({
          id: `candidate-${candidates.length + 1}`,
          label: [getStrategyMeta(strategy).shortLabel, ...(straddle ? ['ATM'] : []), ...labels].join(' · '),
          inputs: current,
        })
        return
      }
      const axis = activeAxes[index]
      const meta = parameterMeta.get(axis.parameter)!
      for (const value of axis.values) {
        const displayValue = Number((value * meta.scale).toPrecision(8))
        expand(index + 1, { ...current, [axis.parameter]: value }, [
          ...labels,
          `${meta.label} ${displayValue}${meta.unit ?? ''}`,
        ])
      }
    }
    expand(0, inputs, [])
  }
  return candidates
}

/** Stable ranking; unavailable/nonfinite observations always sort after valid scores. */
export function rankOptimizationRows(
  rows: OptimizationRow[],
  objective: OptimizationObjective,
  direction?: 'asc' | 'desc',
): OptimizationRow[] {
  const meta = OPTIMIZATION_OBJECTIVES.find((entry) => entry.key === objective)
  if (!meta) throw new Error('不支持的优化目标。')
  const order = direction ?? meta.direction
  if (order !== 'asc' && order !== 'desc') throw new Error('排序方向无效。')
  return rows.map((row, index) => ({ row, index })).sort((a, b) => {
    const aScore = a.row.summary[objective]
    const bScore = b.row.summary[objective]
    const aValid = typeof aScore === 'number' && Number.isFinite(aScore)
    const bValid = typeof bScore === 'number' && Number.isFinite(bScore)
    if (aValid !== bValid) return aValid ? -1 : 1
    if (!aValid || !bValid) return a.index - b.index
    return (order === 'asc' ? aScore - bScore : bScore - aScore) || a.index - b.index
  }).map(({ row }) => row)
}

export function runOptimization(
  base: SimulationInputs,
  config: OptimizationConfig,
  onProgress?: (progress: number, completed: number, total: number) => void,
): OptimizationResult {
  const started = performance.now()
  const candidates = buildOptimizationCandidates(base, config)
  const total = candidates.length
  onProgress?.(0, 0, total)
  const rows = candidates.map((candidate, index): OptimizationRow => {
    const summary = runSimulationSummary(candidate.inputs, (progress) => {
      onProgress?.((index * 100 + progress) / total, index + (progress === 100 ? 1 : 0), total)
    })
    return { ...candidate, summary }
  })
  return {
    base: { ...base },
    config: {
      strategies: [...config.strategies],
      axes: config.axes.map((axis) => ({ parameter: axis.parameter, values: [...axis.values] })),
      paths: config.paths,
    },
    rows,
    elapsedMs: performance.now() - started,
  }
}
